import Logo from "@/components/Logo";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Bookmark, BookmarkCheck, Sparkles, RefreshCw, ExternalLink, Newspaper,
  CalendarClock, Flame, Grid3x3, Activity, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TVWidget } from "@/components/TradingView";
import { UpgradeWall } from "@/components/FeatureGate";
import { AnalysisCard } from "@/components/AnalysisCard";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARKETS = [
  { id: "all", label: "All Markets" },
  { id: "forex", label: "Forex" },
  { id: "crypto", label: "Crypto" },
  { id: "futures", label: "Futures" },
  { id: "stocks", label: "Stocks" },
];

const TV_SCREENER_MARKET = { all: "america", forex: "forex", crypto: "crypto", futures: "futures", stocks: "america" };

function timeAgo(ts) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Card({ title, icon: Icon, children, className = "", testid }) {
  return (
    <div className={`glass-card p-0 overflow-hidden flex flex-col ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-white/8">
        {Icon && <Icon strokeWidth={1.5} className="h-4 w-4 text-[#9fd3ff]" />}
        <span className="section-label text-[10px] text-zinc-300">{title}</span>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

export default function Intelligence() {
  const { user, hasFeature } = useAuth();
  const hasIntel = hasFeature("intelligence");
  const hasSetups = hasFeature("trade_setups");
  const [market, setMarket] = useState("all");
  const [analysis, setAnalysis] = useState([]);
  const [setups, setSetups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sumLoading, setSumLoading] = useState(true);
  const [news, setNews] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [alertsOn, setAlertsOn] = useState(false);

  const bmUrls = new Set(bookmarks.map((b) => b.url));

  const loadSummary = useCallback(async (m) => {
    setSumLoading(true);
    setSummary(null);
    try {
      const r = await fetch(`${API}/intelligence/summary?market=${m}`);
      const d = await r.json();
      setSummary(r.ok ? d.text : "The briefing is unavailable right now. Please try again shortly.");
    } catch {
      setSummary("The briefing is unavailable right now.");
    } finally {
      setSumLoading(false);
    }
  }, []);

  const loadNews = useCallback(async (m) => {
    try {
      const r = await fetch(`${API}/intelligence/news?market=${m}&limit=36`);
      setNews(await r.json());
    } catch {
      setNews([]);
    }
  }, []);

  const loadBookmarks = useCallback(async () => {
    if (!user) return setBookmarks([]);
    try {
      const r = await fetch(`${API}/intelligence/bookmarks`, { credentials: "include" });
      if (r.ok) setBookmarks(await r.json());
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { loadNews(market); }, [market, loadNews]);
  useEffect(() => { if (hasIntel) loadSummary(market); }, [market, hasIntel, loadSummary]);
  useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

  useEffect(() => {
    if (!hasIntel || !user) return setAnalysis([]);
    fetch(`${API}/analysis/feed?market=${market}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAnalysis)
      .catch(() => setAnalysis([]));
  }, [market, hasIntel, user]);

  useEffect(() => {
    if (!hasSetups || !user) return setSetups([]);
    fetch(`${API}/analysis/setups`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setSetups)
      .catch(() => setSetups([]));
  }, [hasSetups, user]);

  const toggleBookmark = async (item) => {
    if (!user) { toast("Log in to save headlines", { description: "Bookmarks are saved to your account." }); return; }
    const existing = bookmarks.find((b) => b.url === item.url);
    if (existing) {
      await fetch(`${API}/intelligence/bookmarks/${existing.id}`, { method: "DELETE", credentials: "include" });
      setBookmarks((b) => b.filter((x) => x.id !== existing.id));
    } else {
      const r = await fetch(`${API}/intelligence/bookmarks`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title: item.title, url: item.url, source: item.source, market }),
      });
      if (r.ok) { await loadBookmarks(); toast.success("Saved to bookmarks"); }
    }
  };

  const enableAlerts = async () => {
    if (!("Notification" in window)) { toast("Notifications not supported on this device"); return; }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      setAlertsOn(true);
      localStorage.setItem("mbm_alerts", "1");
      new Notification("MBM Intelligence", { body: "Morning briefing alerts are on. We'll notify you when the desk opens." });
      toast.success("Briefing alerts enabled");
    } else {
      toast("Enable notifications in your browser to get alerts");
    }
  };

  useEffect(() => { setAlertsOn(localStorage.getItem("mbm_alerts") === "1"); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#9fd3ff]">Intelligence</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/community" className="text-xs text-zinc-400 hover:text-white transition-colors hidden sm:block" data-testid="intel-community-link">Community</Link>
            <button
              onClick={enableAlerts}
              className={`flex items-center gap-2 px-3.5 py-2 text-[11px] tracking-wide border rounded-lg transition-colors ${alertsOn ? "border-[#7ee0a1]/40 text-[#7ee0a1]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}
              data-testid="intel-alerts-btn"
            >
              <Bell className="h-3.5 w-3.5" /> {alertsOn ? "Alerts On" : "Enable Alerts"}
            </button>
            {user ? (
              <span className="section-label text-[10px] text-zinc-500 hidden sm:block" data-testid="intel-saved-count">
                {bookmarks.length} saved
              </span>
            ) : (
              <Link to="/login" className="btn-solid px-4 py-2 text-[11px] font-medium tracking-wide">Login</Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="section-label text-[10px] text-zinc-500">Your morning briefing</div>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
          The market, before it opens.
        </h1>

        {/* Market filter */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="intel-market-filter">
          {MARKETS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMarket(m.id)}
              className={`px-4 py-2 text-xs tracking-wide rounded-full border transition-colors duration-300 ${
                market === m.id ? "bg-[#FAFAFA] text-[#0A0A0A] border-transparent" : "border-white/15 text-zinc-400 hover:border-white/30"
              }`}
              data-testid={`intel-tab-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* AI Market Summary */}
        {hasIntel ? (
        <div className="mt-8 glass-card glass-card-featured p-6 sm:p-8" data-testid="intel-ai-summary">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Sparkles strokeWidth={1.5} className="h-4 w-4 text-[#9fd3ff]" />
              <span className="section-label text-[10px] text-zinc-200">AI Market Summary</span>
            </div>
            <button onClick={() => loadSummary(market)} className="text-zinc-500 hover:text-white transition-colors" data-testid="intel-summary-refresh">
              <RefreshCw className={`h-4 w-4 ${sumLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="mt-5 text-sm sm:text-[15px] text-zinc-200 font-light leading-relaxed whitespace-pre-wrap min-h-[80px]" data-testid="intel-summary-text">
            {sumLoading ? "Generating today's briefing…" : summary}
          </div>
          <div className="mt-5 text-[11px] text-zinc-600 font-light">
            Educational analysis and decision support only. Not financial advice, not a buy or sell recommendation.
          </div>
        </div>
        ) : (
          <div className="mt-8" data-testid="intel-ai-summary-locked">
            <UpgradeWall
              feature="intelligence"
              title="Unlock the daily AI briefing"
              blurb="The AI Market Summary distils the day's headlines into a plain-language briefing for analysis and decision support. Headlines and market data below are free to browse — the AI briefing unlocks with MBM Intelligence (included in Academy, Pro and Elite)."
            />
          </div>
        )}

        {/* AI Market Analysis cards */}
        {hasIntel && analysis.length > 0 && (
          <div className="mt-10" data-testid="intel-analysis-section">
            <div className="flex items-center gap-2.5 mb-1">
              <Sparkles strokeWidth={1.5} className="h-4 w-4 text-[#8fd3b8]" />
              <span className="section-label text-[10px] text-zinc-200">AI Market Analysis</span>
            </div>
            <p className="text-[12px] text-zinc-500 font-light mb-5">Auto-generated pattern studies from real hourly market data. Illustrative teaching examples only — never live trade calls.</p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {analysis.map((c) => <AnalysisCard key={c.id} card={c} badge="MBM INTELLIGENCE" />)}
            </div>
          </div>
        )}

        {/* MBM Trade Setups (coach-published) */}
        {user && hasSetups && setups.length > 0 && (
          <div className="mt-10" data-testid="intel-setups-section">
            <div className="flex items-center gap-2.5 mb-1">
              <TrendingUp strokeWidth={1.5} className="h-4 w-4 text-[#e6c67a]" />
              <span className="section-label text-[10px] text-zinc-200">MBM Trade Setups</span>
            </div>
            <p className="text-[12px] text-zinc-500 font-light mb-5">Coach-published educational setup breakdowns for decision support.</p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {setups.map((c) => <AnalysisCard key={c.id} card={c} badge="MBM TRADE SETUPS" />)}
            </div>
          </div>
        )}
        {user && hasIntel && !hasSetups && (
          <div className="mt-8 glass-card p-4 flex flex-wrap items-center gap-3" data-testid="setups-teaser">
            <div className="flex-1 min-w-[220px] text-[12px] text-zinc-400 font-light">
              <span className="text-zinc-100 font-medium">MBM Trade Setups</span> — coach-published educational setup breakdowns unlock with the Trade Setups add-on.
            </div>
            <Link to="/#pricing" className="btn-glass text-white px-4 py-2 text-xs">View add-ons</Link>
          </div>
        )}

        {/* Saved headlines */}
        {user && bookmarks.length > 0 && (
          <div className="mt-8 glass-card p-6" data-testid="intel-bookmarks">
            <div className="flex items-center gap-2.5 mb-4">
              <BookmarkCheck strokeWidth={1.5} className="h-4 w-4 text-[#7ee0a1]" />
              <span className="section-label text-[10px] text-zinc-300">Saved Headlines</span>
            </div>
            <div className="space-y-3">
              {bookmarks.map((b) => (
                <div key={b.id} className="flex items-start gap-3 group">
                  <a href={b.url} target="_blank" rel="noreferrer" className="flex-1 text-sm text-zinc-300 hover:text-white font-light transition-colors">
                    {b.title} <span className="text-zinc-600 text-xs">· {b.source}</span>
                  </a>
                  <button onClick={() => toggleBookmark(b)} className="text-[#7ee0a1] hover:text-red-400 transition-colors">
                    <BookmarkCheck className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Native headlines (multi-source, live) */}
        <div className="mt-8">
          <Card title="Market News Wire" icon={Newspaper} testid="intel-headlines">
            <div className="max-h-[560px] overflow-y-auto grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-y divide-white/5">
              {news.length === 0 && <div className="p-5 text-sm text-zinc-500 font-light">Loading headlines…</div>}
              {news.map((n) => (
                <div key={n.id} className="flex items-start gap-3 px-5 py-4 hover:bg-white/[0.02] transition-colors" data-testid="intel-headline-item">
                  <button onClick={() => toggleBookmark(n)} className="mt-0.5 text-zinc-500 hover:text-[#9fd3ff] transition-colors" data-testid={`intel-bookmark-${n.id}`}>
                    {bmUrls.has(n.url) ? <BookmarkCheck className="h-4 w-4 text-[#7ee0a1]" /> : <Bookmark className="h-4 w-4" />}
                  </button>
                  <div className="flex-1">
                    <a href={n.url} target="_blank" rel="noreferrer" className="text-sm text-zinc-200 hover:text-white font-light leading-snug transition-colors inline-flex gap-1">
                      {n.title}
                      <ExternalLink className="h-3 w-3 text-zinc-600 shrink-0 mt-1" />
                    </a>
                    <div className="mt-1 section-label text-[8px] text-zinc-600">
                      {n.source}
                      {n.published_ts > 0 && <span className="text-[#9fd3ff]/70"> · {timeAgo(n.published_ts)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Calendar + Movers */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Economic Calendar & Central Banks" icon={CalendarClock} testid="intel-calendar">
            <TVWidget
              src="https://s3.tradingview.com/external-embedding/embed-widget-events.js"
              height={460}
              config={{ colorTheme: "dark", isTransparent: true, locale: "en", importanceFilter: "0,1",
                        countryFilter: "us,eu,gb,jp,au,ca,cn,ch", width: "100%", height: "100%" }}
            />
          </Card>
          <Card title="Market Movers — Gainers, Losers & Active" icon={Flame} testid="intel-movers">
            <TVWidget
              src="https://s3.tradingview.com/external-embedding/embed-widget-hotlists.js"
              height={460}
              config={{ colorTheme: "dark", dateRange: "1D", exchange: "US", showChart: true, locale: "en",
                        isTransparent: true, showSymbolLogo: true, width: "100%", height: "100%" }}
            />
          </Card>
        </div>

        {/* Heatmap + Currency strength */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title={market === "crypto" ? "Crypto Heat Map" : "Market Heat Map"} icon={Grid3x3} testid="intel-heatmap">
            {market === "crypto" ? (
              <TVWidget
                key="crypto-heat"
                src="https://s3.tradingview.com/external-embedding/embed-widget-crypto-coins-heatmap.js"
                height={480}
                config={{ dataSource: "Crypto", blockSize: "market_cap_calc", blockColor: "change", locale: "en",
                          colorTheme: "dark", hasTopBar: false, isDataSetEnabled: false, isZoomEnabled: true, isTransparent: true, width: "100%", height: "100%" }}
              />
            ) : (
              <TVWidget
                src="https://s3.tradingview.com/external-embedding/embed-widget-stock-heatmap.js"
                height={480}
                config={{ dataSource: "SPX500", blockSize: "market_cap_basic", blockColor: "change", grouping: "sector",
                          locale: "en", colorTheme: "dark", hasTopBar: false, isZoomEnabled: true, isTransparent: true, width: "100%", height: "100%" }}
              />
            )}
          </Card>
          <Card title="Currency Strength" icon={Activity} testid="intel-currency">
            <TVWidget
              src="https://s3.tradingview.com/external-embedding/embed-widget-forex-heat-map.js"
              height={480}
              config={{ currencies: ["EUR", "USD", "JPY", "GBP", "CHF", "AUD", "CAD", "NZD"],
                        isTransparent: true, colorTheme: "dark", locale: "en", width: "100%", height: "100%" }}
            />
          </Card>
        </div>

        {/* Trending / Most volatile screener */}
        <div className="mt-6">
          <Card title="Trending & Most Volatile Assets" icon={TrendingUp} testid="intel-screener">
            <TVWidget
              key={`screener-${market}`}
              src="https://s3.tradingview.com/external-embedding/embed-widget-screener.js"
              height={520}
              config={{ market: TV_SCREENER_MARKET[market], showToolbar: true, defaultColumn: "overview",
                        defaultScreen: "most_capitalized", colorTheme: "dark", isTransparent: true, locale: "en", width: "100%", height: "100%" }}
            />
          </Card>
        </div>

        <p className="mt-10 text-xs text-zinc-600 font-light max-w-3xl">
          MBM Intelligence provides market data, news and educational commentary for analysis and
          decision support only. Nothing here is financial advice or a buy or sell recommendation.
          Data and news are provided by third parties.
        </p>
      </main>
    </div>
  );
}
